import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useData } from '../../contexts/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiDollarSign, FiSave, FiCheckCircle, FiLoader, FiAlertTriangle, FiChevronDown } = FiIcons;

const PersonalInfo = () => {
  const { personalInfo, updatePersonalInfo, countries, currencies } = useData();
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    date_of_birth: '',
    retirement_age: '',
    life_expectancy: '',
    gender: '',
    tax_residency_country_id: '',
    employment_status: '',
    investment_currency: '',
    current_investment_value: '',
    recurring_investment_amount: '',
    investment_frequency: '',
    number_of_dependents: '',
    marital_status: ''
  });

  useEffect(() => {
    if (personalInfo) {
      setFormData(personalInfo);
    }
  }, [personalInfo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    try {
      await updatePersonalInfo(formData);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving personal info:', err);
      setError('Failed to save information: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const employmentTypes = ["Full-time", "Part-time", "Self-employed", "Unemployed", "Retired", "Student"];
  const frequencies = ["Monthly", "Quarterly", "Bi-Annually", "Yearly"];
  const dependents = ["0", "1", "2", "3", "4", "5", "6", ">6"];
  const maritalStatuses = ["Single", "Married", "Divorced", "Widowed", "Civil Partnership"];

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-secondary-900 rounded-2xl shadow-xl border border-primary-500/20 overflow-hidden"
    >
      <div className="p-8 border-b border-primary-500/10 flex justify-between items-center bg-black/20">
        <div>
          <h2 className="text-2xl font-bold text-white">Personal Information</h2>
          <p className="text-secondary-400 text-sm mt-1">Keep your financial profile up to date for precise projections.</p>
        </div>
        <div className={`flex items-center space-x-2 text-sm ${personalInfo ? 'text-green-500' : 'text-primary-500'}`}>
          <SafeIcon icon={personalInfo ? FiCheckCircle : FiUser} className="w-5 h-5" />
          <span className="font-bold uppercase tracking-wider">{personalInfo ? 'Profile Complete' : 'New Profile'}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-8 space-y-8">
        {error && (
          <div className="bg-red-900/20 border border-red-500/20 text-red-400 p-4 rounded-xl flex items-center space-x-3">
            <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 flex-shrink-0" />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Date of Birth</label>
            <input 
              type="date" 
              name="date_of_birth" 
              value={formData.date_of_birth || ''} 
              onChange={handleChange} 
              className="w-full bg-black border border-secondary-800 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" 
              required 
            />
          </div>
          <div className="relative">
            <label className="block text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Gender</label>
            <select 
              name="gender" 
              value={formData.gender || ''} 
              onChange={handleChange} 
              className="w-full bg-black border border-secondary-800 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all appearance-none" 
              required 
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
            <SafeIcon icon={FiChevronDown} className="absolute right-4 top-10 text-secondary-600 pointer-events-none" />
          </div>
          <div className="relative">
            <label className="block text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Marital Status</label>
            <select 
              name="marital_status" 
              value={formData.marital_status || ''} 
              onChange={handleChange} 
              className="w-full bg-black border border-secondary-800 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all appearance-none" 
              required 
            >
              <option value="">Select Status</option>
              {maritalStatuses.map(status => <option key={status} value={status}>{status}</option>)}
            </select>
            <SafeIcon icon={FiChevronDown} className="absolute right-4 top-10 text-secondary-600 pointer-events-none" />
          </div>
        </div>

        {/* Planning Targets */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Retirement Age</label>
            <input 
              type="number" 
              name="retirement_age" 
              value={formData.retirement_age || ''} 
              onChange={handleChange} 
              placeholder="e.g. 65"
              className="w-full bg-black border border-secondary-800 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" 
              required 
            />
          </div>
          <div>
            <label className="block text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Life Expectancy</label>
            <input 
              type="number" 
              name="life_expectancy" 
              value={formData.life_expectancy || ''} 
              onChange={handleChange} 
              placeholder="e.g. 85"
              className="w-full bg-black border border-secondary-800 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" 
              required 
            />
          </div>
          <div className="relative">
            <label className="block text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Dependents</label>
            <select 
              name="number_of_dependents" 
              value={formData.number_of_dependents || ''} 
              onChange={handleChange} 
              className="w-full bg-black border border-secondary-800 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all appearance-none" 
              required 
            >
              <option value="">Number of Dependents</option>
              {dependents.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <SafeIcon icon={FiChevronDown} className="absolute right-4 top-10 text-secondary-600 pointer-events-none" />
          </div>
        </div>

        {/* Residency & Employment */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="relative">
            <label className="block text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Tax Residency Country</label>
            <select 
              name="tax_residency_country_id" 
              value={formData.tax_residency_country_id || ''} 
              onChange={handleChange} 
              className="w-full bg-black border border-secondary-800 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all appearance-none" 
              required 
            >
              <option value="">Select Country</option>
              {countries.map(c => <option key={c.id} value={c.id}>{c.country_name}</option>)}
            </select>
            <SafeIcon icon={FiChevronDown} className="absolute right-4 top-10 text-secondary-600 pointer-events-none" />
          </div>
          <div className="relative">
            <label className="block text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Employment Status</label>
            <select 
              name="employment_status" 
              value={formData.employment_status || ''} 
              onChange={handleChange} 
              className="w-full bg-black border border-secondary-800 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all appearance-none" 
              required 
            >
              <option value="">Select Status</option>
              {employmentTypes.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <SafeIcon icon={FiChevronDown} className="absolute right-4 top-10 text-secondary-600 pointer-events-none" />
          </div>
        </div>

        {/* Investments */}
        <div className="space-y-6 pt-6 border-t border-secondary-800">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative">
              <label className="block text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Investment Currency</label>
              <select 
                name="investment_currency" 
                value={formData.investment_currency || ''} 
                onChange={handleChange} 
                className="w-full bg-black border border-secondary-800 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all appearance-none" 
                required 
              >
                <option value="">Select Currency</option>
                {currencies.map(curr => <option key={curr} value={curr}>{curr}</option>)}
              </select>
              <SafeIcon icon={FiChevronDown} className="absolute right-4 top-10 text-secondary-600 pointer-events-none" />
            </div>
            <div className="relative">
              <label className="block text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Investment Frequency</label>
              <select 
                name="investment_frequency" 
                value={formData.investment_frequency || ''} 
                onChange={handleChange} 
                className="w-full bg-black border border-secondary-800 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all appearance-none" 
                required 
              >
                <option value="">Select Frequency</option>
                {frequencies.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <SafeIcon icon={FiChevronDown} className="absolute right-4 top-10 text-secondary-600 pointer-events-none" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Current Value of Investments</label>
              <div className="relative">
                <input 
                  type="number" 
                  name="current_investment_value" 
                  value={formData.current_investment_value || ''} 
                  onChange={handleChange} 
                  placeholder="0.00"
                  className="w-full bg-black border border-secondary-800 rounded-xl px-4 py-3 pl-10 text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" 
                  required 
                />
                <SafeIcon icon={FiDollarSign} className="absolute left-3 top-3.5 text-primary-500 w-4 h-4" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Recurring Investment Amount</label>
              <div className="relative">
                <input 
                  type="number" 
                  name="recurring_investment_amount" 
                  value={formData.recurring_investment_amount || ''} 
                  onChange={handleChange} 
                  placeholder="0.00"
                  className="w-full bg-black border border-secondary-800 rounded-xl px-4 py-3 pl-10 text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all" 
                  required 
                />
                <SafeIcon icon={FiDollarSign} className="absolute left-3 top-3.5 text-primary-500 w-4 h-4" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end pt-6">
          <motion.button 
            whileTap={{ scale: 0.98 }}
            type="submit" 
            disabled={isSaving}
            className="bg-primary-500 hover:bg-primary-400 text-black px-10 py-4 rounded-xl font-black flex items-center space-x-3 transition-all shadow-lg shadow-primary-500/20 disabled:opacity-50"
          >
            {isSaving ? (
              <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
            ) : (
              <SafeIcon icon={FiSave} className="w-5 h-5" />
            )}
            <span>{personalInfo ? 'Update Information' : 'Save Information'}</span>
          </motion.button>
        </div>

        {showSuccess && (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="fixed bottom-8 right-8 bg-green-500 text-black px-6 py-4 rounded-xl font-bold shadow-2xl flex items-center space-x-3 z-50"
          >
            <SafeIcon icon={FiCheckCircle} className="w-6 h-6" />
            <span>Information saved successfully!</span>
          </motion.div>
        )}
      </form>
    </motion.div>
  );
};

export default PersonalInfo;